/**
 * useActivePlugin - resolve the currently active plugin id from the URL.
 *
 * This hook deliberately mirrors the resolution logic in
 * `plugins/PluginActivityRouter.tsx` so that the Guide Me overlay agrees with
 * the plugin router about which plugin is rendered at any given time.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPluginManifests } from '../plugins/registry';
import type { PluginManifest } from '../plugins/types';

function getPluginIdFromPath(pathname: string): string | null {
  // Root path renders the host-app Home page, not a plugin. Map it to a
  // synthetic 'home' id so Guide Me can attach tours to /.
  if (pathname === '/' || pathname === '') return 'home';
  const parts = pathname.split('/').filter(Boolean);
  return parts[0] || null;
}

function pluginOwnsPathSegment(routes: Array<{ path: string }>, pathSegment: string): boolean {
  const prefix = `/${pathSegment}`;
  return routes.some((r) =>
    r.path === prefix || r.path.startsWith(`${prefix}/`) || r.path.startsWith(`${prefix}:`),
  );
}

function resolvePluginIdFromPath(pathname: string, manifests: PluginManifest[]): string | null {
  const pathSegment = getPluginIdFromPath(pathname);
  if (!pathSegment) return null;

  for (const manifest of manifests) {
    if (manifest.id === pathSegment) return manifest.id;
    if (manifest.primary_route && pathname.startsWith(manifest.primary_route)) return manifest.id;
    if (manifest.primary_route === `/${pathSegment}`) return manifest.id;

    const routes = manifest.contributions?.routes;
    if (routes) {
      for (const route of routes) {
        if (pathname === route.path || pathname.startsWith(`${route.path}/`)) return manifest.id;
        if (route.path.includes(':')) {
          const baseRoute = route.path.split('/:')[0];
          if (pathname === baseRoute || pathname.startsWith(`${baseRoute}/`)) return manifest.id;
        }
      }
      if (pluginOwnsPathSegment(routes, pathSegment)) return manifest.id;
    }
  }
  return pathSegment;
}

export interface ActivePluginInfo {
  pluginId: string | null;
  pathname: string;
  /** Stable key suitable for `localStorage` keys (pluginId + pathname). */
  routeKey: string;
}

export function useActivePlugin(): ActivePluginInfo {
  const location = useLocation();
  const [manifests, setManifests] = useState<PluginManifest[]>([]);

  useEffect(() => {
    let cancelled = false;
    getPluginManifests()
      .then((list) => {
        if (!cancelled) setManifests(list);
      })
      .catch(() => {
        if (!cancelled) setManifests([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const pluginId = useMemo(
    () => resolvePluginIdFromPath(location.pathname, manifests),
    [location.pathname, manifests],
  );

  return {
    pluginId,
    pathname: location.pathname,
    routeKey: `${pluginId ?? 'core'}::${location.pathname}`,
  };
}
